import { useCallback, useEffect, useState } from "react";
import {
  Circle,
  Contrast,
  Feather,
  Moon,
  Sun,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * Theme is two orthogonal axes, each a named variant persisted to its own
 * `localStorage` key and applied as its own class on `<html>`:
 *
 * - **mode** (`mode` key → `.light` class; dark is the unclassed default):
 *   the light/dark tone of the UI.
 * - **palette** (`palette` key → `.palette-<name>` class; `default` is
 *   unclassed): a named complete look. `default` is the brand-colored
 *   outlet look (parchment + deep teal + editorial type), promoted from
 *   the `outlet` palette in the previous theme system. `mono` is the
 *   neutral monochrome look; `classic` is the legacy PowerUp ramp;
 *   `modern` is the prior-default Tailwind-ish ramp. New palettes drop
 *   in as a registry entry + a CSS block — see `PALETTE_VARIANTS` below
 *   and `docs/DESIGN-SYSTEM.md` § "Theme model".
 *
 * The two classes are the single source of truth — every token-backed
 * utility resolves off them, so no `dark:` / palette variants are needed at
 * call sites. The pre-paint script in `index.html` mirrors this logic to
 * apply both classes before first paint (avoids a flash).
 */

export type Mode = "light" | "dark";

export type PaletteId = "default" | "classic" | "modern" | "mono";

export interface ModeVariant {
  id: Mode;
  label: string;
  icon: LucideIcon;
}

export interface PaletteVariant {
  id: PaletteId;
  label: string;
  icon: LucideIcon;
  /** Class applied to `<html>`. `null` for the default (unclassed) palette. */
  className: string | null;
}

/** Registry — the menu renders straight off these. Order = display order. */
export const MODE_VARIANTS: readonly ModeVariant[] = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
];

export const PALETTE_VARIANTS: readonly PaletteVariant[] = [
  // Default is the base palette — editorial parchment + deep teal +
  // editorial type, modeled on the Outlet energy-desk reference. No
  // class needed; an unclassed `<html>` falls through to this look,
  // which is also what `:root` / `.light` in `index.css` define.
  { id: "default", label: "Default", icon: Feather, className: null },
  // Classic — legacy PowerUp ramp; the prior fresh-install default.
  { id: "classic", label: "Classic", icon: Zap, className: "palette-classic" },
  // Modern — Tailwind-ish dark-first ramp that used to be unclassed.
  // Now an opt-in override.
  { id: "modern", label: "Modern", icon: Circle, className: "palette-modern" },
  // Monochrome — neutral grey chrome; brand accents ride through.
  { id: "mono", label: "Monochrome", icon: Contrast, className: "palette-mono" },
];

const MODE_IDS = new Set<string>(MODE_VARIANTS.map((m) => m.id));
const PALETTE_IDS = new Set<string>(PALETTE_VARIANTS.map((p) => p.id));
const ALL_PALETTE_CLASSES = PALETTE_VARIANTS.map((p) => p.className).filter(
  (c): c is string => c != null,
);

function readStoredMode(): Mode {
  try {
    const raw = localStorage.getItem("mode");
    return raw && MODE_IDS.has(raw) ? (raw as Mode) : "light";
  } catch {
    return "light";
  }
}

function readStoredPalette(): PaletteId {
  try {
    const raw = localStorage.getItem("palette");
    return raw && PALETTE_IDS.has(raw) ? (raw as PaletteId) : "default";
  } catch {
    return "default";
  }
}

export function useTheme() {
  const [mode, setModeState] = useState<Mode>(readStoredMode);
  const [palette, setPaletteState] = useState<PaletteId>(readStoredPalette);

  useEffect(() => {
    document.documentElement.classList.toggle("light", mode === "light");
    try {
      localStorage.setItem("mode", mode);
    } catch {
      // ignore
    }
  }, [mode]);

  useEffect(() => {
    const el = document.documentElement;
    const active = PALETTE_VARIANTS.find((p) => p.id === palette);
    // Swap exactly one palette class on, the rest off — so switching between
    // named palettes never stacks two override layers.
    for (const cls of ALL_PALETTE_CLASSES) {
      el.classList.toggle(cls, cls === active?.className);
    }
    try {
      localStorage.setItem("palette", palette);
    } catch {
      // ignore
    }
  }, [palette]);

  const setMode = useCallback((next: Mode) => setModeState(next), []);
  const setPalette = useCallback((next: PaletteId) => setPaletteState(next), []);

  return { mode, setMode, palette, setPalette };
}
